import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { CelebrationService, CelebrationType } from './celebration.service';

interface Firework {
  id: number;
  x: number;
  y: number;
  launchDelay: number;
  color: string;
  particles: Particle[];
}

interface Particle {
  id: number;
  tx: number;
  ty: number;
  color: string;
  size: number;
  delay: number;
}

@Component({
  selector: 'app-celebration',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (isActive) {
      <div class="fireworks-overlay" (click)="dismiss()">
        @for (fw of fireworks; track fw.id) {
          <!-- Rocket going up -->
          <div class="rocket"
               [style.left.%]="fw.x"
               [style.bottom.%]="0"
               [style.animation-delay]="fw.launchDelay + 's'"
               [style.--target-y]="fw.y + '%'"
               [style.--rocket-color]="fw.color">
          </div>

          <!-- Explosion particles -->
          @for (p of fw.particles; track p.id) {
            <div class="particle"
                 [style.left.%]="fw.x"
                 [style.bottom.%]="fw.y"
                 [style.animation-delay]="(fw.launchDelay + 0.8) + 's'"
                 [style.--tx]="p.tx + 'px'"
                 [style.--ty]="p.ty + 'px'"
                 [style.--color]="p.color"
                 [style.width.px]="p.size"
                 [style.height.px]="p.size">
            </div>
          }
        }

        <!-- Center message -->
        <div class="celebration-message" [class.enrolled-theme]="celebrationType === 'enrolled'">
          <div class="trophy">{{ celebrationType === 'enrolled' ? '🎓' : '🏆' }}</div>
          <div class="text">{{ celebrationType === 'enrolled' ? 'ENROLLED!' : 'WON!' }}</div>
        </div>

        <!-- Close button -->
        <button class="close-btn" (click)="dismiss(); $event.stopPropagation()">
          ✕
        </button>
      </div>
    }
  `,
  styles: [`
    .fireworks-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: all;
      cursor: default;
      z-index: 99999;
      overflow: hidden;
      background: rgba(0, 0, 0, 0.4);
    }

    /* Close button */
    .close-btn {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.6);
      background: rgba(0, 0, 0, 0.5);
      color: #fff;
      font-size: 18px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: all;
      transition: all 0.2s ease;
      z-index: 100000;
      animation: fade-in 0.8s ease-out both;
    }

    .close-btn:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: #fff;
      transform: scale(1.1);
    }

    @keyframes fade-in {
      0% { opacity: 0; }
      100% { opacity: 1; }
    }

    /* Rocket shooting up */
    .rocket {
      position: absolute;
      width: 4px;
      height: 4px;
      background: var(--rocket-color, #fff);
      border-radius: 50%;
      opacity: 0;
      animation: launch 0.8s ease-out forwards;
      box-shadow:
        0 0 6px 2px var(--rocket-color, #fff),
        0 0 12px 4px var(--rocket-color, #fff);
    }

    .rocket::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      width: 2px;
      height: 40px;
      background: linear-gradient(to bottom, var(--rocket-color, orange), transparent);
    }

    @keyframes launch {
      0% {
        bottom: 0%;
        opacity: 1;
      }
      95% {
        opacity: 1;
      }
      100% {
        bottom: var(--target-y, 60%);
        opacity: 0;
      }
    }

    /* Explosion particles */
    .particle {
      position: absolute;
      background: var(--color, #fff);
      border-radius: 50%;
      opacity: 0;
      box-shadow:
        0 0 6px 2px var(--color, #fff),
        0 0 10px 4px var(--color, #fff);
      animation: explode 1.5s ease-out forwards;
    }

    @keyframes explode {
      0% {
        opacity: 1;
        transform: translate(0, 0) scale(1);
      }
      100% {
        opacity: 0;
        transform: translate(var(--tx, 0), var(--ty, 100px)) scale(0.2);
      }
    }

    /* Center message */
    .celebration-message {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 5px;
      animation: pop-in 0.6s ease-out 0.5s both;
      text-align: center;
    }

    @keyframes pop-in {
      0% {
        transform: translate(-50%, -50%) scale(0);
        opacity: 0;
      }
      60% {
        transform: translate(-50%, -50%) scale(1.15);
      }
      100% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 1;
      }
    }

    .trophy {
      font-size: 80px;
      filter: drop-shadow(0 0 30px rgba(255, 215, 0, 0.9));
      display: block;
      line-height: 1;
      animation: trophy-glow 1.5s ease-in-out infinite;
    }

    @keyframes trophy-glow {
      0%, 100% { filter: drop-shadow(0 0 20px rgba(255, 215, 0, 0.8)); }
      50% { filter: drop-shadow(0 0 40px rgba(255, 215, 0, 1)); }
    }

    .text {
      font-size: 48px;
      font-weight: 900;
      color: #FFD700;
      text-shadow:
        2px 2px 4px rgba(0,0,0,0.5),
        0 0 20px rgba(255, 215, 0, 0.5);
      letter-spacing: 8px;
      display: block;
      line-height: 1;
      margin-top: 10px;
    }

    /* Enrolled Theme */
    .celebration-message.enrolled-theme {
      .trophy {
        filter: drop-shadow(0 0 30px rgba(0, 191, 255, 0.9));
        animation: enrolled-glow 1.5s ease-in-out infinite;
      }

      .text {
        color: #00BFFF;
        text-shadow:
          2px 2px 4px rgba(0,0,0,0.5),
          0 0 20px rgba(0, 191, 255, 0.5);
      }
    }

    @keyframes enrolled-glow {
      0%, 100% { filter: drop-shadow(0 0 20px rgba(0, 191, 255, 0.8)); }
      50% { filter: drop-shadow(0 0 40px rgba(0, 191, 255, 1)); }
    }

    @media (max-width: 768px) {
      .trophy { font-size: 60px; }
      .text { font-size: 36px; letter-spacing: 4px; }

      .close-btn {
        top: 12px;
        right: 12px;
        width: 36px;
        height: 36px;
        font-size: 16px;
      }
    }
  `]
})
export class CelebrationComponent implements OnInit, OnDestroy {
  isActive = false;
  fireworks: Firework[] = [];
  celebrationType: CelebrationType = 'won';

  private subscription: Subscription;
  private timeout: any;

  private wonColors = [
    '#FFD700', // Gold
    '#FF6B6B', // Red/Coral
    '#4ECDC4', // Teal
    '#FF69B4', // Pink
    '#00FF7F', // Spring Green
    '#FF4500', // Orange Red
    '#9B59B6', // Purple
    '#00BFFF', // Deep Sky Blue
    '#FFFFFF', // White
  ];

  private enrolledColors = [
    '#00BFFF', // Deep Sky Blue
    '#00CED1', // Dark Turquoise
    '#4ECDC4', // Teal
    '#00FF7F', // Spring Green
    '#7FFFD4', // Aquamarine
    '#40E0D0', // Turquoise
    '#00FA9A', // Medium Spring Green
    '#FFFFFF', // White
    '#87CEEB', // Sky Blue
  ];

  constructor(
    private celebrationService: CelebrationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.subscription = this.celebrationService.celebrate$.subscribe((type) => {
      this.celebrationType = type;
      this.triggerFireworks();
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    if (this.timeout) clearTimeout(this.timeout);
  }

  dismiss() {
    this.isActive = false;
    this.fireworks = [];
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.cdr.detectChanges();
  }

  private triggerFireworks() {
    this.isActive = true;
    this.generateFireworks();
    this.cdr.detectChanges();

    this.timeout = setTimeout(() => {
      this.dismiss();
    }, 5000);
  }

  private generateFireworks() {
    this.fireworks = [];
    const colors = this.celebrationType === 'enrolled' ? this.enrolledColors : this.wonColors;

    for (let i = 0; i < 15; i++) {
      const color = colors[Math.floor(Math.random() * colors.length)];
      const x = 8 + Math.random() * 84;
      const y = 50 + Math.random() * 35;

      this.fireworks.push({
        id: i,
        x: x,
        y: y,
        launchDelay: (i % 5) * 0.4 + Math.random() * 0.3,
        color: color,
        particles: this.generateParticles(color)
      });
    }
  }

  private generateParticles(baseColor: string): Particle[] {
    const particles: Particle[] = [];
    const count = 30;

    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const distance = 80 + Math.random() * 60;

      particles.push({
        id: i,
        tx: Math.cos(angle) * distance,
        ty: -Math.sin(angle) * distance + 60,
        color: baseColor,
        size: 3 + Math.random() * 4,
        delay: Math.random() * 0.1
      });
    }

    return particles;
  }
}